import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaGift,
  FaUserFriends,
  FaBolt,
  FaChevronLeft,
} from "react-icons/fa";
import { doc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../firebase";import TopBar from "../components/common/TopBar";
import BottomNav from "./BottomNav";
import goldCoin from "../assets/gold.png";
import "./MyRankRewardsPage.css";

const RECHARGE_REWARDS = [
  {
    id: "recharge_500_coins",
    category: "recharge",
    target: 500,
    rewardType: "coins",
    rewardValue: 15,
    title: "Recharge 500 Coins",
    icon: "recharge",
  },
  {
    id: "recharge_1000_coins",
    category: "recharge",
    target: 1000,
    rewardType: "coins",
    rewardValue: 30,
    title: "Recharge 1000 Coins",
    icon: "coin",
  },
  {
    id: "recharge_3000_coins",
    category: "recharge",
    target: 3000,
    rewardType: "coins",
    rewardValue: 80,
    title: "Recharge 3000 Coins",
    icon: "coin",
  },
  {
    id: "recharge_5000_coins",
    category: "recharge",
    target: 5000,
    rewardType: "coins",
    rewardValue: 150,
    title: "Recharge 5000 Coins",
    icon: "gift",
  },
];

const INVITE_REWARDS = [
  {
    id: "invite_5_coins",
    category: "invite",
    target: 5,
    rewardType: "coins",
    rewardValue: 20,
    title: "Invite 5 friends",
    icon: "invite",
  },
  {
    id: "invite_10_coins",
    category: "invite",
    target: 10,
    rewardType: "coins",
    rewardValue: 50,
    title: "Invite 10 friends",
    icon: "invite",
  },
  {
    id: "invite_20_coins",
    category: "invite",
    target: 20,
    rewardType: "coins",
    rewardValue: 120,
    title: "Invite 20 friends",
    icon: "gift",
  },
];

function getNumeric(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export default function MyRankRewardsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState("recharge");
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState("");
  const [gender, setGender] = useState("");
  const [inviteCount, setInviteCount] = useState(0);
  const [totalRechargeCoins, setTotalRechargeCoins] = useState(0);
  const [claimedRewardIds, setClaimedRewardIds] = useState([]);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    const user = auth.currentUser;

    if (!user) {
      navigate("/");
      return;
    }

    const userRef = doc(db, "users", user.uid);

    const unsub = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) {
          setLoading(false);
          navigate("/");
          return;
        }

        const data = snap.data() || {};
        const userGender = String(data.gender || "").toLowerCase();

        setGender(userGender);
        setInviteCount(getNumeric(data.inviteCount, 0));
        setTotalRechargeCoins(
          getNumeric(
            data.maleTotalRecharge ??
              data.totalRechargeCoins ??
              data.coinsSpentTotal,
            0
          )
        );
        setClaimedRewardIds(
          Array.isArray(data.claimedRewardIds) ? data.claimedRewardIds : []
        );

        setLoading(false);

        if (userGender === "female") {
          navigate("/my-profile");
        }
      },
      (error) => {
        console.error("MyRankRewardsPage snapshot error:", error);
        setLoading(false);
        navigate("/");
      }
    );

    return () => unsub();
  }, [navigate]);

const functionsEurope = useMemo(
  () => getFunctions(undefined, "europe-west1"),
  []
);

const claimJourneyReward = useMemo(
  () => httpsCallable(functionsEurope, "claimJourneyReward"),
  [functionsEurope]
);

  const enrichReward = (reward) => {
    const progress =
      reward.category === "invite" ? inviteCount : totalRechargeCoins;
    const claimed = claimedRewardIds.includes(reward.id);
    const ready = !claimed && progress >= reward.target;

    return {
      ...reward,
      title:
        reward.category === "recharge"
          ? t("rewards.rank.rechargeCoins", {
              defaultValue: reward.title,
              amount: reward.target,
            })
          : reward.category === "invite"
          ? t("rewards.rank.inviteFriends", {
              defaultValue: reward.title,
              count: reward.target,
            })
          : reward.title,
      progress,
      claimed,
      ready,
      cta: claimed
        ? t("rewards.status.claimed", { defaultValue: "طھظ… ط§ظ„ط§ط³طھظ„ط§ظ…" })
        : ready
        ? t("rewards.actions.claimNow", { defaultValue: "ط§ط³طھظ„ط§ظ… ط§ظ„ط¢ظ†" })
        : t("rewards.status.inProgress", { defaultValue: "ظ‚ظٹط¯ ط§ظ„طھظ‚ط¯ظ…" }),
    };
  };

  const rechargeRewards = useMemo(
    () => RECHARGE_REWARDS.map(enrichReward),
    [totalRechargeCoins, claimedRewardIds, t]
  );

  const inviteRewards = useMemo(
    () => INVITE_REWARDS.map(enrichReward),
    [inviteCount, claimedRewardIds, t]
  );

  const claimableRewards = useMemo(
    () => [...rechargeRewards, ...inviteRewards].filter((r) => r.ready),
    [rechargeRewards, inviteRewards]
  );

  const claimableCoins = useMemo(
    () =>
      claimableRewards.reduce(
        (sum, reward) =>
          reward.rewardType === "coins" ? sum + reward.rewardValue : sum,
        0
      ),
    [claimableRewards]
  );

  const list = activeTab === "recharge" ? rechargeRewards : inviteRewards;

  const upcomingRewards = useMemo(() => {
    return [...rechargeRewards, ...inviteRewards]
      .filter((item) => !item.claimed && !item.ready)
      .sort((a, b) => {
        const aLeft = Math.max(0, a.target - a.progress);
        const bLeft = Math.max(0, b.target - b.progress);
        return aLeft - bLeft;
      })
      .slice(0, 3);
  }, [rechargeRewards, inviteRewards]);

  const handleClaim = async (rewardId) => {
    if (!rewardId) return;

    try {
      setFeedback("");
      setClaimingId(rewardId);

      const res = await claimJourneyReward({ rewardId });
      const data = res?.data || {};

      setFeedback(
        data?.message ||
          t("rewards.messages.claimSuccess", {
            defaultValue: "طھظ… ط§ط³طھظ„ط§ظ… ط§ظ„ظ…ظƒط§ظپط£ط© ط¨ظ†ط¬ط§ط­.",
          })
      );
    } catch (error) {
      console.error("claimJourneyReward error:", error);

      const msg =
        error?.message ||
        t("rewards.messages.claimError", {
          defaultValue: "ط­ط¯ط« ط®ط·ط£ ط£ط«ظ†ط§ط، ط§ط³طھظ„ط§ظ… ط§ظ„ظ…ظƒط§ظپط£ط©.",
        });

      setFeedback(msg);
    } finally {
      setClaimingId("");
    }
  };

  const handleClaimAllReady = async () => {
    for (const reward of claimableRewards) {
      // eslint-disable-next-line no-await-in-loop
      await handleClaim(reward.id);
    }
  };

  const renderCardIcon = (type) => {
    if (type === "invite") {
      return (
        <div className="rank-reward-card-art invite">
          <FaUserFriends />
        </div>
      );
    }

    if (type === "gift") {
      return (
        <div className="rank-reward-card-art gift">
          <FaGift />
        </div>
      );
    }

    if (type === "recharge") {
      return (
        <div className="rank-reward-card-art recharge">
          <FaBolt />
        </div>
      );
    }

    return (
      <div className="rank-reward-card-art coin">
        <img src={goldCoin} alt={t("rewards.rank.coinAlt", { defaultValue: "Coins" })} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="rank-rewards-page">
        <TopBar
          title={t("rewards.rank.title", { defaultValue: "ط±ط­ظ„طھظٹ ظپظٹ Livora" })}
          onBack={() => navigate(-1)}
        />
        <div className="rank-rewards-content">
          <div className="rank-loading-state">
            {t("common.loading", { defaultValue: "ط¬ط§ط±ظٹ ط§ظ„طھط­ظ…ظٹظ„..." })}
          </div>
        </div>
        <BottomNav active="profile" />
      </div>
    );
  }

  if (gender === "female") {
    return null;
  }

  return (
    <div className="rank-rewards-page">
      <TopBar
        title={t("rewards.rank.title", { defaultValue: "ط±ط­ظ„طھظٹ ظپظٹ Livora" })}
        onBack={() => navigate(-1)}
      />

      <div className="rank-rewards-content">
        {!!feedback && <div className="rank-feedback-box">{feedback}</div>}

        <section className="rank-alert-banner">
          <div className="rank-alert-left">
            <div className="rank-alert-coin-wrap">
              <img
                src={goldCoin}
                alt={t("rewards.rank.coinAlt", { defaultValue: "Coins" })}
                className="rank-alert-coin"
              />
            </div>
          </div>

          <div className="rank-alert-texts">
            <div className="rank-alert-title">
              {claimableCoins > 0
                ? t("rewards.rank.claimableCoinsReady", {
                    defaultValue: "ظ„ط¯ظٹظƒ {{coins}} Coins ط¬ط§ظ‡ط²ط© ظ„ظ„ط§ط³طھظ„ط§ظ…!",
                    coins: claimableCoins,
                  })
                : t("rewards.empty.claimable", {
                    defaultValue: "ظ„ط§ طھظˆط¬ط¯ ظ…ظƒط§ظپط¢طھ ط¬ط§ظ‡ط²ط© ط§ظ„ط¢ظ†",
                  })}
            </div>

            <div className="rank-alert-subtitle">
              {claimableCoins > 0
                ? t("rewards.rank.claimableSubtitle", {
                    defaultValue: "ط£ظ†ظ‡ظگ ظ…ظƒط§ظپط¢طھظƒ ط§ظ„ط¢ظ† ظˆط§ط±ط¨ط­ ط§ظ„ظ…ط²ظٹط¯ ظ…ظ† ط§ظ„ط¬ظˆط§ط¦ط².",
                  })
                : t("rewards.rank.emptyClaimableSubtitle", {
                    defaultValue:
                      "ط£ظƒظ…ظ„ ط§ظ„ط´ط­ظ† ط£ظˆ ط¯ط¹ظˆط© ط§ظ„ط£طµط¯ظ‚ط§ط، ظ„ظپطھط­ ط§ظ„ظ…ظƒط§ظپط¢طھ ط§ظ„ظ‚ط§ط¯ظ…ط©.",
                  })}
            </div>
          </div>

          <button
            className="rank-alert-btn"
            onClick={handleClaimAllReady}
            disabled={!claimableRewards.length || !!claimingId}
          >
            {claimingId
              ? t("common.loading", { defaultValue: "ط¬ط§ط±ظٹ ط§ظ„طھظ†ظپظٹط°..." })
              : t("rewards.actions.claimNow", { defaultValue: "ط§ط³طھظ„ط§ظ… ط§ظ„ط¢ظ†" })}
          </button>
        </section>

        <section className="rank-tabs-section">
          <div className="rank-tabs">
            <button
              className={`rank-tab ${activeTab === "recharge" ? "active" : ""}`}
              onClick={() => setActiveTab("recharge")}
            >
              {t("rewards.categories.recharge", { defaultValue: "ظ…ظƒط§ظپط¢طھ ط§ظ„ط´ط­ظ†" })}
            </button>

            <button
              className={`rank-tab ${activeTab === "invite" ? "active" : ""}`}
              onClick={() => setActiveTab("invite")}
            >
              {t("rewards.categories.invite", { defaultValue: "ظ…ظƒط§ظپط¢طھ ط§ظ„ط¯ط¹ظˆط§طھ" })}
            </button>
          </div>
        </section>

        <section className="rank-rewards-list">
          {list.map((item) => {
            const percent = Math.max(
              0,
              Math.min(100, Math.round((item.progress / item.target) * 100))
            );

            return (
              <div
                key={item.id}
                className={`rank-reward-card ${item.ready ? "ready" : ""} ${
                  item.claimed ? "claimed" : ""
                }`}
              >
                <div className="rank-reward-top">
                  <div className="rank-reward-main">
                    {renderCardIcon(item.icon)}

                    <div className="rank-reward-texts">
                      <div className="rank-reward-title">{item.title}</div>
                      <div className="rank-reward-subtitle">
                        {t("rewards.rank.rewardValueCoins", {
                          defaultValue: "ط§ط±ط¨ط­ {{value}} Coins",
                          value: item.rewardValue,
                        })}
                      </div>
                    </div>
                  </div>

                  <button
                    className={`rank-reward-cta ${
                      item.ready ? "ready" : ""
                    } ${item.claimed ? "claimed" : ""}`}
                    disabled={!item.ready || !!claimingId}
                    onClick={() => handleClaim(item.id)}
                  >
                    {claimingId === item.id
                      ? t("common.loading", { defaultValue: "ط¬ط§ط±ظٹ ط§ظ„طھظ†ظپظٹط°..." })
                      : item.cta}
                  </button>
                </div>

                <div className="rank-reward-progress">
                  <div className="rank-reward-progress-bar">
                    <div
                      className="rank-reward-progress-fill"
                      style={{ width: `${percent}%` }}
                    />
                  </div>

                  <div className="rank-reward-progress-value">
                    {item.progress} / {item.target}
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        <section className="rank-upcoming-section">
          <div className="rank-section-head">
            <div className="rank-section-title">
              {t("rewards.upcoming.title", { defaultValue: "ط§ظ„ط¬ظˆط§ط¦ط² ط§ظ„ظ‚ط§ط¯ظ…ط©" })}
            </div>

            <button className="rank-section-link" type="button">
              <span>{t("rewards.actions.viewAll", { defaultValue: "ط¹ط±ط¶ ط§ظ„ظƒظ„" })}</span>
              <FaChevronLeft />
            </button>
          </div>

          <div className="rank-upcoming-list">
            {upcomingRewards.map((item) => {
              const percent = Math.max(
                0,
                Math.min(100, Math.round((item.progress / item.target) * 100))
              );

              return (
                <div key={item.id} className="rank-upcoming-card">
                  <div className="rank-upcoming-icon">
                    <img src={goldCoin} alt={t("rewards.rank.coinAlt", { defaultValue: "Coins" })} />
                  </div>

                  <div className="rank-upcoming-body">
                    <div className="rank-upcoming-title">{item.title}</div>
                    <div className="rank-upcoming-subtitle">
                      {t("rewards.rank.rewardValueCoins", {
                        defaultValue: "ط§ط±ط¨ط­ {{value}} Coins",
                        value: item.rewardValue,
                      })}
                    </div>

                    <div className="rank-upcoming-progress-row">
                      <div className="rank-upcoming-progress-bar">
                        <div
                          className="rank-upcoming-progress-fill"
                          style={{ width: `${percent}%` }}
                        />
                      </div>

                      <div className="rank-upcoming-progress-value">
                        {item.progress} / {item.target}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {!upcomingRewards.length && (
              <div className="rank-empty-upcoming">
                {t("rewards.empty.upcoming", {
                  defaultValue: "ظ„ط§ طھظˆط¬ط¯ ظ…ظƒط§ظپط¢طھ ظ‚ط§ط¯ظ…ط© ط­ط§ظ„ظٹظ‹ط§.",
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      <BottomNav active="profile" />
    </div>
  );
}
